// The initiative pop-up (kanban d71a3beb, owner round 4: "a true pop-up ...
// a lot of well-organized detail"). Replaces the side drawer for both the
// list and the timeline. Sections in the order the owner reads them: summary,
// dates and sub-actions, decisions, waiting on, holds up, people, evidence,
// Merlin's questions (with the existing answer / dismiss controls), history.
// Cross-links open the other initiative in place with a back link; Escape
// closes; under 640px it is a full-screen sheet.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  roadmapReportsApi,
  type ClarifyingQuestion,
  type InitiativeEvent,
  type InitiativeState,
} from '../../services/roadmapReports';
import { ACTION_STATUS_LABELS, actionBlockerRefs, describeChanges, liveActions, mandateAreaTitle, ownerLine, STATUS_LABELS, type MandateLookup, type TeamColors, accentTeamOf } from './roadmapViewUtils';
import { actionSpan, fmtDay, fmtDayYear, groupEvidence, initiativeSpan, isoDay, parseDay, type EvidenceSource } from './roadmapTimelineUtils';
import { HealthChip } from './HealthChip';
import { EvidenceList, type OpenSourceFn } from './EvidenceList';
import { CitedSources } from './CitedSources';
import { evidenceForInitiative, evidenceForQuestion } from './evidenceUtils';
import { TeamsEditor } from './TeamsEditor';
import { ParkControl } from './ParkControl';
import { QuestionResponseControls, type RespondFn, type WithdrawFn } from './QuestionResponseControls';
import { useModalKeys } from './useModalKeys';
import type { ModalSection } from './TimelineChart';
import tableStyles from '../RoadmapReportsPage.module.css';
import styles from './RoadmapV2.module.css';

export interface InitiativeModalProps {
  initiative: InitiativeState;
  all: InitiativeState[];
  teamColors: TeamColors;
  /** Open another initiative from a cross-link (the page keeps the back stack). */
  onSelect: (key: string, section?: ModalSection, action?: string) => void;
  onClose: () => void;
  /** Where a cross-link came from; renders the back link. */
  backTo?: { key: string; title: string } | null;
  onBack?: () => void;
  /** Section to scroll into view when opened from a glyph. */
  initialSection?: ModalSection;
  /** The source ledger, so evidence reads as a meeting's name instead of its id. */
  sourceLedger?: readonly EvidenceSource[];
  /** Merlin's questions for this initiative (from the questions endpoint). */
  questions?: ClarifyingQuestion[];
  onRespond?: RespondFn;
  /** Which of the item's asks this reader may answer; omitted → all of them. */
  canRespondTo?: (q: ClarifyingQuestion) => boolean;
  onWithdraw?: WithdrawFn;
  knownTeams?: string[];
  /** Edit the teams (kanban ae374351): every roadmap:edit-teams holder, not only admins (owner ask 2026-10-21). */
  canEditTeams?: boolean;
  /** Admins: park / un-park (kanban cac323f9). */
  canPark?: boolean;
  onTeamsChanged?: () => void | Promise<void>;
  /** Opens the source viewer at a quote (kanban 9eb5f005). */
  onOpenSource?: OpenSourceFn;
  /** The mandate registry (v10), for the teams editor's area select. */
  mandates?: MandateLookup;
  /** Injectable clock for tests. */
  today?: string;
}

const SECTIONS: { id: ModalSection; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'dates', label: 'Dates and sub-actions' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'waiting', label: 'Waiting on' },
  { id: 'holds', label: 'Holds up' },
  { id: 'people', label: 'People' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'questions', label: "Merlin's questions" },
  { id: 'history', label: 'History' },
];

/** Healths worth a second chip beside the status: the schedule ones. "No
 * target", "Parked", "Done" and "Dropped" only repeat the status or the date line. */
const SHOWN_HEALTH: ReadonlySet<string> = new Set(['late', 'at_risk', 'stuck', 'on_track']);

const PRIORITY_LABELS: Record<NonNullable<InitiativeState['priority']>, string> = {
  top: 'Top priority',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

interface HistoryState {
  key: string | null;
  events: InitiativeEvent[] | null;
  failed: boolean;
}

const EMPTY_HISTORY: HistoryState = { key: null, events: null, failed: false };

function MiniTimeline({ i, today, color, onOpen }: { i: InitiativeState; today: Date; color: string; onOpen: (actionKey: string) => void }) {
  const actions = liveActions(i.actions);
  const spans = actions.map((a) => ({ a, s: actionSpan(a) }));
  const dated = spans.filter((x) => x.s.dated);
  if (dated.length === 0) return null;
  const span = initiativeSpan(i, today);
  const times = [
    ...dated.flatMap((x) => [x.s.start, x.s.end].filter((d): d is Date => Boolean(d)).map((d) => d.getTime())),
    today.getTime(),
    ...(span.dated ? [span.start.getTime(), span.end.getTime()] : []),
  ];
  const lo = Math.min(...times);
  const hi = Math.max(...times);
  const range = Math.max(1, hi - lo);
  const X = (d: Date) => 40 + ((d.getTime() - lo) / range) * 780;
  const H = 22 * actions.length + 30;
  return (
    <svg className={styles.miniTimeline} viewBox={`0 0 880 ${H}`} role="img" aria-label="Sub-action dates">
      <line x1={X(today)} x2={X(today)} y1={10} y2={H} stroke="#4a3aa7" strokeWidth={1.5} />
      <text x={X(today) + 4} y={12} fontSize={10} fill="#4a3aa7">today</text>
      {spans.map(({ a, s }, n) => {
        const y = 30 + n * 22;
        if (!s.dated) {
          return (
            <g key={a.key} onClick={() => onOpen(a.key)} data-action="roadmap_action_open" data-target-initiative-key={i.initiative_key} data-target-action-key={a.key}>
              <title>Open this sub-action</title>
              <text x={40} y={y + 4} fontSize={11} fill="#8a8985" fontStyle="italic">no dates stated</text>
              <text x={200} y={y + 4} fontSize={11} fill="#52514e">{a.title.slice(0, 70)}</text>
            </g>
          );
        }
        const st = s.start ?? (s.end as Date);
        const en = s.end ?? (s.start as Date);
        const w = Math.max(6, X(en) - X(st));
        return (
          <g key={a.key} onClick={() => onOpen(a.key)} data-action="roadmap_action_open" data-target-initiative-key={i.initiative_key} data-target-action-key={a.key}>
            <title>Open this sub-action</title>
            <rect x={X(st)} y={y - 6} width={w} height={12} rx={6} fill={a.status === 'done' ? color : `${color}55`} stroke={color} />
            <text x={Math.min(X(st) + w + 6, 640)} y={y + 4} fontSize={11} fill="#52514e">
              {a.title.slice(0, 60)} · {ACTION_STATUS_LABELS[a.status] ?? a.status}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function InitiativeModal({
  initiative: i,
  all,
  teamColors,
  onSelect,
  onClose,
  backTo = null,
  onBack,
  initialSection = 'summary',
  sourceLedger = [],
  questions = [],
  onRespond,
  canRespondTo,
  onWithdraw,
  knownTeams = [],
  canEditTeams = false,
  canPark = false,
  onTeamsChanged,
  onOpenSource,
  mandates = {},
  today: todayIso,
}: InitiativeModalProps) {
  const today = useMemo(() => parseDay(todayIso ?? isoDay(new Date())) as Date, [todayIso]);
  const decisions = i.decisions ?? [];
  const externalBlocker = i.external_blocker ?? null;
  const byKey = useMemo(() => new Map(all.map((x) => [x.initiative_key, x])), [all]);
  const holdsUp = useMemo(
    () => all.filter((x) => x.depends_on.includes(i.initiative_key) || i.blocks.includes(x.initiative_key)).map((x) => x.initiative_key),
    [all, i],
  );
  const span = initiativeSpan(i, today);
  const laneColor = teamColors[accentTeamOf(i) ?? ''] ?? '#94a3b8';
  const evidence = useMemo(() => groupEvidence(i.sources), [i.sources]);
  const quotes = i.evidence ?? [];
  // Quotes that back the initiative itself come first; the ones attached to a
  // question or a sub-action are listed under what they back.
  const initiativeQuotes = useMemo(() => evidenceForInitiative(quotes), [quotes]);
  const attachedQuotes = quotes.length - initiativeQuotes.length;
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const [history, setHistory] = useState<HistoryState>(EMPTY_HISTORY);
  const { events, failed } = history.key === i.initiative_key ? history : EMPTY_HISTORY;

  useEffect(() => {
    let cancelled = false;
    const key = i.initiative_key;
    roadmapReportsApi
      .getInitiativeHistory(key)
      .then((res) => { if (!cancelled) setHistory({ key, events: res.events, failed: false }); })
      .catch(() => { if (!cancelled) setHistory({ key, events: null, failed: true }); });
    return () => { cancelled = true; };
  }, [i.initiative_key, i.event_count]);

  // Escape closes (from a text box it only leaves the box); Tab stays inside the dialog (focus trap).
  useModalKeys(dialogRef, onClose);

  // Focus the dialog on open and scroll the requested section into view.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const target = dialog.querySelector<HTMLElement>(`#modal-section-${initialSection}`);
    if (target && initialSection !== 'summary' && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'start' });
    }
    dialog.focus();
  }, [i.initiative_key, initialSection]);

  const link = (k: string) => {
    const other = byKey.get(k);
    if (!other) return <span className={styles.mono}>{k}</span>;
    return (
      <>
        <button type="button" className={styles.linkBtn} onClick={() => onSelect(k, 'summary')}>{other.title}</button>{' '}
        <span className={`${tableStyles.statusChip} ${tableStyles[`status_${other.status}`] ?? ''}`}>{STATUS_LABELS[other.status]}</span>{' '}
        <span className={styles.meta}>{other.target_date ? `target ${other.target_date}` : 'no stated target'}</span>
      </>
    );
  };

  const canRespond = Boolean(onRespond && onWithdraw);

  return (
    <>
      <div className={styles.modalBackdrop} onClick={onClose} data-testid="modal-backdrop" />
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={`Initiative: ${i.title}`}
        tabIndex={-1}
        data-testid="initiative-modal"
      >
        <header className={styles.modalHead}>
          <div className={styles.modalHeadMain}>
            {backTo && (
              <button type="button" className={styles.backBtn} onClick={onBack} data-testid="modal-back">
                &larr; Back to {backTo.title}
              </button>
            )}
            <h2>{i.title}</h2>
            <div className={styles.chips}>
              <span className={`${tableStyles.statusChip} ${tableStyles[`status_${i.status}`] ?? ''}`}>{STATUS_LABELS[i.status]}</span>
              {/* The health chip only when it adds something: "In progress" + "No target" said the missing date twice. */}
              {SHOWN_HEALTH.has(i.health) && <HealthChip health={i.health} initiative={i} />}
              {i.priority && <span className={styles.priorityTag} data-priority={i.priority}>{PRIORITY_LABELS[i.priority]}</span>}
              <span className={styles.meta}>{ownerLine(i)}</span>
              {mandateAreaTitle(i, mandates) && (
                <span className={styles.meta} title="The owning team's mandate area this work is filed under" data-testid="modal-mandate">
                  filed under {mandateAreaTitle(i, mandates)}
                </span>
              )}
            </div>
            <div className={styles.meta} data-testid="modal-dates">
              {span.placeholder
                ? `No stated dates. Tracked since ${fmtDayYear(span.start)}.`
                : `${span.startStated ? `Started ${fmtDayYear(span.start)}` : `Tracked since ${fmtDayYear(span.start)} (start not stated)`} · ${span.endStated ? `Target ${fmtDayYear(span.end)} (stated)` : 'No stated target'}`}
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} data-action="roadmap_initiative_close" data-outcome="close">
            Close
          </button>
        </header>

        <nav className={styles.modalNav} aria-label="Sections">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#modal-section-${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                dialogRef.current?.querySelector(`#modal-section-${s.id}`)?.scrollIntoView?.({ block: 'start' });
              }}
            >
              {s.label}
            </a>
          ))}
        </nav>

        <div className={styles.modalBody}>
          <section id="modal-section-summary" className={styles.section}>
            <h3>Summary</h3>
            <TeamsEditor
              key={i.initiative_key}
              initiative={i}
              teamColors={teamColors}
              knownTeams={knownTeams}
              canEdit={canEditTeams}
              onChanged={onTeamsChanged}
              mandates={mandates}
            />
            <ParkControl key={`park-${i.initiative_key}`} initiative={i} canEdit={canPark} onChanged={onTeamsChanged} />
            {i.summary ? <p className={styles.summaryText}>{i.summary}</p> : <div className={styles.muted}>No summary recorded.</div>}
          </section>

          <section id="modal-section-dates" className={styles.section}>
            <h3>Dates and sub-actions</h3>
            <dl className={styles.kv}>
              <dt>Started</dt><dd>{i.started_on ?? <span className={styles.muted}>not stated · tracked since {i.first_seen_at.slice(0, 10)}</span>}</dd>
              <dt>Target</dt><dd>{i.target_date ?? (i.status === 'parked'
                ? <span className={styles.muted}>none while parked</span>
                : i.status === 'ongoing'
                ? <span className={styles.muted}>none: ongoing work, the sub-actions carry the dates</span>
                : <span className={styles.needsTarget}>no stated target. Merlin is asking</span>)}</dd>
              <dt>Last movement</dt><dd>{fmtDayYear(new Date(i.updated_at))}</dd>
            </dl>
            {liveActions(i.actions).length === 0 ? (
              <div className={styles.muted}>No separate actions named. The initiative is the action.</div>
            ) : (
              <>
                <MiniTimeline i={i} today={today} color={laneColor} onOpen={(k) => onSelect(i.initiative_key, 'dates', k)} />
                <ul className={styles.actionList} aria-label="Sub-actions">
                  {liveActions(i.actions).map((a) => {
                    const s = actionSpan(a);
                    const blockers = actionBlockerRefs(a, liveActions(i.actions));
                    const openAction = (key: string) => onSelect(i.initiative_key, 'dates', key);
                    return (
                      <li key={a.key}>
                        <span className={`${styles.actionStatus} ${styles[`action_${a.status}`] ?? ''}`}>{ACTION_STATUS_LABELS[a.status] ?? a.status}</span>{' '}
                        {/* The title opens the sub-action's own pop-up (owner, 2026-10-20: clickable everywhere it shows up). */}
                        <button
                          type="button"
                          className={styles.actionLink}
                          onClick={() => openAction(a.key)}
                          title="Open this sub-action"
                          data-action="roadmap_action_open"
                          data-target-initiative-key={i.initiative_key}
                          data-target-action-key={a.key}
                        >
                          <strong>{a.title}</strong>
                        </button>
                        <span className={styles.meta}>
                          {' '}· {a.people.length > 0 ? a.people.join(', ') : 'nobody named'}
                          {' '}· {s.dated ? `${s.start ? fmtDay(s.start) : 'start not stated'} to ${s.end ? fmtDay(s.end) : 'end not stated'}` : 'no dates stated'}
                          {blockers.length > 0 && (
                            <>
                              {' '}· after:{' '}
                              {blockers.map((b, n) => (
                                <span key={b.key}>
                                  {n > 0 ? ', ' : ''}
                                  <button type="button" className={styles.actionLink} onClick={() => openAction(b.key)} title="Open this sub-action" data-action="roadmap_action_open" data-target-initiative-key={i.initiative_key} data-target-action-key={b.key}>{b.title}</button>
                                </span>
                              ))}
                            </>
                          )}
                        </span>
                        {a.note && <div className={styles.actionNote}>{a.note}</div>}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>

          <section id="modal-section-decisions" className={styles.section}>
            <h3>Decisions ({decisions.length})</h3>
            {decisions.length === 0 ? (
              <div className={styles.muted}>No decisions recorded yet.</div>
            ) : (
              <ul className={styles.plainList} aria-label="Decisions">
                {decisions.map((d, n) => (
                  <li key={`${d.on ?? ''}-${n}`}>
                    <span className={styles.muted}>{d.on ?? 'date not stated'}</span> {d.what}
                    {d.who.length > 0 && <span className={styles.muted}> ({d.who.join(', ')})</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section id="modal-section-waiting" className={styles.section}>
            <h3>Waiting on</h3>
            {i.status === 'parked' && (i.parked_reason || (!externalBlocker && i.depends_on.length === 0)) && (
              <p className={styles.summaryText}>
                <strong>Parked:</strong> {i.parked_reason ?? 'nobody has said why yet; Merlin asks next run.'}
                {i.park_override && <span className={styles.meta}> (parked by {i.park_override.set_by_email})</span>}
              </p>
            )}
            {externalBlocker && <p className={styles.summaryText}><strong>Outside the roadmap:</strong> {externalBlocker}</p>}
            {!externalBlocker && i.status === 'blocked' && (
              <p className={styles.needsTarget}>Blocked, but nothing named. Merlin is asking.</p>
            )}
            {i.depends_on.length > 0 ? (
              <ul className={styles.linkList} aria-label="Depends on">{i.depends_on.map((k) => <li key={k}>{link(k)}</li>)}</ul>
            ) : (
              !externalBlocker && i.status !== 'blocked' && <div className={styles.muted}>Nothing tracked blocks this.</div>
            )}
            {i.related_to.length > 0 && (
              <p className={styles.meta} style={{ marginTop: 8 }}>
                Related (shared cause or topic): {i.related_to.map((k, n) => <span key={k}>{n > 0 && ', '}{link(k)}</span>)}
              </p>
            )}
          </section>

          <section id="modal-section-holds" className={styles.section}>
            <h3>Holds up</h3>
            {holdsUp.length === 0 ? (
              <div className={styles.muted}>Nothing tracked waits on this.</div>
            ) : (
              <ul className={styles.linkList} aria-label="Holds up">{holdsUp.map((k) => <li key={k}>{link(k)}</li>)}</ul>
            )}
          </section>

          <section id="modal-section-people" className={styles.section}>
            <h3>People</h3>
            <dl className={styles.kv}>
              <dt>Owner</dt><dd>{i.owner ?? <span className={styles.muted}>not named</span>}</dd>
              <dt>Holding actions</dt>
              <dd>{i.people.length ? i.people.map((p) => <span key={p} className={styles.personTag}>{p}</span>) : <span className={styles.muted}>none named</span>}</dd>
            </dl>
          </section>

          <section id="modal-section-evidence" className={styles.section}>
            <h3>Evidence</h3>
            {quotes.length === 0 && (
              <div className={styles.muted} data-testid="evidence-no-quotes">
                No quotes recorded yet. Merlin adds the words behind the summary and each question on its next run.
              </div>
            )}
            {initiativeQuotes.length > 0 && <EvidenceList evidence={initiativeQuotes} onOpenSource={onOpenSource} aria-label="Quotes behind this initiative" />}
            {attachedQuotes > 0 && (
              <div className={styles.muted} data-testid="evidence-attached-note">
                {attachedQuotes} more quote{attachedQuotes === 1 ? '' : 's'} sit{attachedQuotes === 1 ? 's' : ''} under the question{attachedQuotes === 1 ? '' : 's'} and sub-actions they back.
              </div>
            )}
            {i.sources.length === 0 ? (
              <div className={styles.muted}>No sources cited.</div>
            ) : (
              <>
                <div className={styles.evidenceSub}>Cited sources</div>
                {/* One list, each line saying what it is; a per-kind heading
                    ("Meeting") read like a tab (owner, review vhztut). Meetings
                    first, then emails, documents and knowledge-bank entries. */}
                <CitedSources sources={evidence.flatMap(([, list]) => list)} ledger={sourceLedger} onOpenSource={onOpenSource} plain withKind />
              </>
            )}
          </section>

          <section id="modal-section-questions" className={styles.section}>
            <h3>Merlin's questions for this item ({questions.length || i.open_questions.length})</h3>
            {questions.length === 0 && i.open_questions.length === 0 && <div className={styles.muted}>No pending questions.</div>}
            {questions.length > 0
              ? questions.map((q) => (
                <div key={q.question} className={styles.questionCard} data-testid="modal-question">
                  <div>{q.question}</div>
                  <EvidenceList
                    evidence={q.evidence ?? evidenceForQuestion(quotes, q.question)}
                    onOpenSource={onOpenSource}
                    aria-label="What this question rests on"
                    emptyNote="No quote recorded for this question yet."
                  />
                  {canRespond && (!canRespondTo || canRespondTo(q)) ? (
                    <QuestionResponseControls
                      identity={{ initiative_key: q.initiative_key, question: q.question }}
                      response={q.response}
                      onRespond={onRespond as RespondFn}
                      onWithdraw={onWithdraw as WithdrawFn}
                    />
                  ) : q.response ? (
                    <div className={styles.muted}>{q.response.kind === 'dismissed' ? 'Dismissed' : 'Answered'}, waiting for Merlin.</div>
                  ) : null}
                </div>
              ))
              : i.open_questions.map((q) => (
                <div key={q} className={styles.questionCard} data-testid="modal-question">
                  <div>{q}</div>
                  <EvidenceList evidence={evidenceForQuestion(quotes, q)} onOpenSource={onOpenSource} aria-label="What this question rests on" emptyNote="No quote recorded for this question yet." />
                </div>
              ))}
          </section>

          <section id="modal-section-history" className={styles.section}>
            <h3>History ({i.event_count} changes)</h3>
            {failed && <div className={styles.muted}>Could not load history.</div>}
            {!failed && events === null && <div className={styles.muted}>Loading history…</div>}
            {events && events.length === 0 && <div className={styles.muted}>No recorded changes yet.</div>}
            {events && events.length > 0 && (
              <ul className={tableStyles.historyList}>
                {events.map((ev) => (
                  <li key={ev.id}>
                    <span className={tableStyles.historyDate}>{new Date(ev.created_at).toLocaleDateString()}</span>{' '}
                    <span className={tableStyles.historyKind}>{ev.kind}{ev.actor ? ` by ${ev.actor}` : ''}</span>{' '}
                    <span className={tableStyles.historyChanges}>
                      {describeChanges({ ...ev, initiative_key: i.initiative_key, title: i.title, report_title: null, report_date: null }).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

